import React, { useEffect, useState } from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';
import qs from 'query-string';
import { useHistory } from 'react-router';

import {
    TabContainer,
    TabList,
    Tab,
    TabPanel,
    Button,
    Checkbox,
    ChikuHeroMobile,
} from 'wanderu-components';
import { localize } from 'wanderu-i18n';
import {
    BigChevronUp,
} from 'wanderu-icons';

import {
    ROUND_TRIP_SEARCH,
    SEARCH_TYPES,
} from '../../../constants';
import MinimizedWidgetBody from '../MinimizedWidgetBody/MinimizedWidgetBody';
import PassengerSelect from '../PassengerSelect/PassengerSelect';
import RoundTripDatePicker from '../RoundTripDatePicker/RoundTripDatePicker';
import RoundTripInputDropdown from '../RoundTripInputDropdown/RoundTripInputDropdown';
import SearchButton from '../SearchButton/SearchButton';
import WidgetHeader from '../WidgetHeader/WidgetHeader';
import AmplitudeEvents from '../events/SearchWidgetAmplitudeEvents';
import Events from '../events/SearchWidgetEvents';
import {
    getPredictionMetadata,
    shouldShowPricing,
} from '../utils';

import styles from './WidgetBody.scss';

function createPriceClassNameLookup(pricing = {}) {
    const sortedPrices = Object.values(pricing).sort((a, b) => a - b);
    const cheapestPrice = sortedPrices[0];
    const secondCheapestPrice = sortedPrices[1];
    const thirdCheapestPrice = sortedPrices[2];

    return {
        [cheapestPrice]: styles.dateCheapest,
        [secondCheapestPrice]: styles.dateSecondCheapest,
        [thirdCheapestPrice]: styles.dateThirdCheapest,
    };
}

function WidgetBody(props) {
    const {
        ctzChecked = false,
        currency,
        departDate,
        departPricing,
        destinationInput,
        destinationPlaceId,
        destinationPrediction,
        disableButton = false,
        emptyFields: {
            missingDepartDate = false,
            missingDestination = false,
            missingOrigin = false,
            missingReturnDate = false,
        } = {},
        equalInputs = false,
        fetchPrediction = () => undefined,
        headerText,
        i18nMoment,
        i18nMoney,
        locale,
        minimized,
        onDepartDateChange = () => undefined,
        onDestinationChange = () => undefined,
        onDestinationInputChange = () => undefined,
        onErrorMessageClear = () => undefined,
        onOriginChange = () => undefined,
        onOriginInputChange = () => undefined,
        onPassengerChange = () => undefined,
        onReturnDateChange = () => undefined,
        onTabChange = () => undefined,
        onToggle = () => undefined,
        onUserInputChange = () => undefined,
        onUserSearch = () => undefined,
        originInput,
        originPlaceId,
        originPrediction,
        passengersInput,
        returnDate,
        returnPricing,
        searchPage = false,
        searchType,
        setDestinationInput = () => undefined,
        setDestinationPlaceId = () => undefined,
        setOriginInput = () => undefined,
        setOriginPlaceId = () => undefined,
        setPassengersInput = () => undefined,
        sticky = false,
        t,
        tabBlack = false,
        title,
        toggleClicktrips,
    } = props;

    const [loading, setLoading] = useState(true);
    useEffect(() => {
        setLoading(false);
    }, []);
    const isRoundTrip = searchType === ROUND_TRIP_SEARCH;

    let history = useHistory();

    const {
        isValid: isSearchValid,
        errors: searchErrors,
    } = getSearchValidityState();
    const errorMessageExists = missingOrigin
        || missingDestination
        || missingDepartDate
        || missingReturnDate
        || equalInputs;

    function formatPricing(pricing) {
        const priceClassNames = createPriceClassNameLookup(pricing);
        if (!pricing) {
            return undefined;
        }
        return Object.entries(pricing)
            .reduce((obj, [date, price]) => {
                const formattedPrice = i18nMoney(price).format({
                    maximumFractionDigits: 0,
                    minimumFractionDigits: 0,
                });

                return {
                    ...obj,
                    [date]: {
                        className: `${styles.date} ${priceClassNames[price]}`,
                        text: formattedPrice,
                    },
                };
            }, {});
    }

    function handleOriginInputChange(value) {
        if (value) {
            fetchPrediction({
                debounce: true,
                input: value,
                locale,
                meta: getPredictionMetadata(),
            });
        }

        setOriginInput(value);
        onOriginInputChange(value);
    }

    function handleOriginChange(id) {
        const isSelected = (prediction) => prediction.id === id;
        const selected = originPrediction?.find(isSelected);
        const index = originPrediction?.findIndex(isSelected);

        const {
            text,
            placeId,
        } = selected || {};

        Events.inputSelected('origin', {
            address: text,
            index,
            placeId,
            userText: originInput,
        });

        if (!placeId) {
            return;
        }

        AmplitudeEvents.searchFormInteracted({
            interactionType: 'origin_selected',
        });

        setOriginInput(text);
        setOriginPlaceId(placeId);

        onOriginChange(text, placeId);
        onUserInputChange();
    }

    function handlePassengerChange(value) {
        if (String(value) !== String(passengersInput)) {
            AmplitudeEvents.searchFormInteracted({
                interactionType: 'passenger_count_changed',
                updatedFrom: String(passengersInput || '1'),
                updatedTo: String(value),
            });
        }

        setPassengersInput(value);
        onPassengerChange(value);
        onUserInputChange();
    }

    function handleDestinationInputChange(value) {
        if (value) {
            fetchPrediction({
                debounce: true,
                input: value,
                locale,
                meta: getPredictionMetadata(),
            });
        }

        setDestinationInput(value);

        onDestinationInputChange(value);
    }

    function handleDestinationChange(id) {
        if (id === 'explore') {
            Events.clickExplore({ userText: destinationInput });

            const queries = qs.parse(history?.location?.search);
            const queryString = qs.stringify({ ...queries, placeId: originPlaceId });

            // Use a full-page navigation (not client-side routing) so the request
            // reaches Cloudflare, which diverts /[language]/explore traffic to the
            // new service. A client-side history.push would render the legacy
            // Explorer app in-place and skew diverted-traffic metrics.
            window.location.assign(`/${locale}/explore/?${queryString}`);
            return;
        }
        const isSelected = (prediction) => prediction.id === id;
        const selected = destinationPrediction?.find(isSelected);
        const index = destinationPrediction?.findIndex(isSelected);

        const {
            text,
            placeId,
        } = selected || {};

        Events.inputSelected('destination', {
            address: text,
            index,
            placeId,
            userText: destinationInput,
        });

        if (!placeId) {
            return;
        }

        AmplitudeEvents.searchFormInteracted({
            interactionType: 'destination_selected',
        });

        setDestinationInput(text);
        setDestinationPlaceId(placeId);

        onDestinationChange(text, placeId);
        onUserInputChange();
    }

    function handleDepartDateChange(onDepartDate) {
        Events.inputSelected('departDate', onDepartDate);
        AmplitudeEvents.searchFormInteracted({
            interactionType: 'date_selected',
        });

        onDepartDateChange(onDepartDate);
    }

    function handleReturnDateChange(onReturnDate) {
        Events.inputSelected('onReturnDate', onReturnDate);
        AmplitudeEvents.searchFormInteracted({
            interactionType: 'date_selected',
        });

        onReturnDateChange(onReturnDate);
    }

    function onFocus(e) {
        if (e?.target) {
            Events.focusInput(e?.target?.dataset?.id, e?.target?.value);
        }

        if (errorMessageExists) {
            onErrorMessageClear();
        }
    }

    function getSearchValidityState() {
        let isValid = true;
        let errors = {};
        if (!originInput || !destinationInput || !departDate || (isRoundTrip && !returnDate)) {
            isValid = false;
            errors = {
                emptyFields: {
                    missingDepartDate: !departDate,
                    missingDestination: !destinationInput,
                    missingOrigin: !originInput,
                    missingReturnDate: isRoundTrip && !returnDate,
                },
            };
        }

        if (originPlaceId === destinationPlaceId) {
            isValid = false;
            errors = {
                ...errors,
                equalInputs: true,
            };
        }

        return { errors, isValid };
    }

    function submitSearch() {
        if (sticky && minimized && !isSearchValid) {
            return handleToggle();
        }

        fireSearchEvents();
        onUserSearch(isSearchValid, searchErrors);

        return undefined;
    }

    function handleToggle() {
        if (errorMessageExists) {
            onErrorMessageClear();
        }

        onToggle();
    }

    function fireSearchEvents() {
        const inputs = {
            clickTripz: ctzChecked,
            departDate,
            destinationInput,
            destinationPlaceId,
            originInput,
            originPlaceId,
            passengersInput,
            returnDate,
            type: searchType === 'round-trip'
                ? 'Round Trip'
                : 'One Way',
        };

        const errors = {
            equalInputs: !!originInput && originInput === destinationInput,
            missingDepartDate: !departDate,
            missingDestination: !destinationInput,
            missingOrigin: !originInput,
            missingReturnDate: isRoundTrip && !returnDate,
        };

        Events.searchSubmitted(inputs, errors);

        // Only fire Amplitude `Search Submitted` for valid submits. Snowplow
        // captures invalid attempts via the `errors` arg above; the multi-pax
        // experiment uses this event as the homepage→search conversion
        // guardrail, where invalid clicks would inflate the numerator.
        if (isSearchValid) {
            AmplitudeEvents.searchSubmitted({
                arriveAddress: destinationInput,
                arrivePlaceId: destinationPlaceId,
                departAddress: originInput,
                departPlaceId: originPlaceId,
                isOneWay: !isRoundTrip,
                outboundDateSearched: departDate,
                paxCount: parseInt(passengersInput, 10) || 1,
                returnDateSearched: returnDate,
            });
        }
    }

    function switchInputs() {
        Events.clickSwitch({
            departDate,
            destinationInput,
            originInput,
            returnDate,
        });
        AmplitudeEvents.searchFormInteracted({
            interactionType: 'origin_destination_swapped',
        });
        setOriginInput(destinationInput);
        setOriginPlaceId(destinationPlaceId);
        onOriginChange(destinationInput, destinationPlaceId);
        setDestinationInput(originInput);
        setDestinationPlaceId(originPlaceId);
        onDestinationChange(originInput, originPlaceId);
        onUserInputChange();
    }
    function onTabSwitch(onSearchType) {
        const cleanedSearchType = onSearchType.replace('-sticky', '');
        Events.clickSearchType({
            cleanedSearchType,
            departDate,
            destinationInput,
            originInput,
            returnDate,
        });
        AmplitudeEvents.searchFormInteracted({
            interactionType: 'trip_type_changed',
            updatedTo: cleanedSearchType.replace(/-/g, '_'),
        });
        onFocus();
        onTabChange(cleanedSearchType);
    }

    function renderDatePicker(isSticky) {
        let departData;
        let returnData;

        if (shouldShowPricing(currency)) {
            departData = formatPricing(departPricing);
            returnData = formatPricing(returnPricing);
        }

        const onDateSelectorFocus = (message) => {
            Events.focusDateSelector({
                departDate,
                destinationInput,
                isRoundTrip,
                originInput,
                returnDate,
            });
            onFocus(message);
        };

        return (
            <RoundTripDatePicker
                cellClass={classNames(styles.cell, {
                    [styles.roundTripDatePickerCell]: isRoundTrip,
                    [styles.oneWayDatePickerCell]: !isRoundTrip,
                })}
                departData={departData}
                departDate={departDate}
                highlightClass={styles.highlighted}
                isRoundTrip={isRoundTrip}
                loading={loading}
                locale={locale}
                minDate={i18nMoment().format()}
                missingDepartDate={missingDepartDate}
                missingReturnDate={missingReturnDate}
                onDepartChange={handleDepartDateChange}
                onFocus={onDateSelectorFocus}
                onReturnChange={handleReturnDateChange}
                returnData={returnData}
                returnDate={returnDate}
                rowClass={classNames(
                    styles.row,
                    { [styles.hasRoundTrip]: isRoundTrip },
                )}
                searchType={searchType}
                sticky={isSticky}
            />
        );
    }

    return (
        <div className={classNames(
            styles.container,
            { [styles.stickyContainer]: sticky },
        )}>
            {headerText && (
                <WidgetHeader>
                    {headerText}
                </WidgetHeader>
            )}
            <TabContainer
                activeId={sticky
                    ? `${searchType}-sticky`
                    : searchType}
                onChange={onTabSwitch}
            >
                <TabList
                    className={classNames(
                        styles.tabs,
                        { [styles.hideTabsOnSticky]: sticky && minimized },
                    )}
                >
                    {SEARCH_TYPES.map((type) => (
                        <Tab
                            activeClassName={styles.active}
                            className={classNames(
                                styles.tab,
                                { [styles.tabBlack]: tabBlack },
                            )}
                            id={sticky
                                ? `${type}-sticky`
                                : type}
                            key={type}
                        >
                            <label className={styles.tabButtonContainer}>
                                <input
                                    checked={type === searchType}
                                    name={sticky
                                        ? 'radio-sticky'
                                        : 'radio'}
                                    type="radio"
                                    readOnly
                                />
                                <span className={
                                    classNames(
                                        styles.tabButton,
                                        { [styles.tabButtonBlack]: tabBlack },
                                    )
                                }
                                />
                                <span>{t(type)}</span>
                            </label>
                        </Tab>

                    ))}
                </TabList>
                <div
                    className={classNames(
                        styles.inputsContainer,
                        {
                            [styles.minimized]: sticky && minimized,
                            [styles.expandedOnMobile]: sticky,
                        },
                    )}
                >
                    <div
                        className={classNames(
                            styles.inputDropdownRow,
                            {
                                [styles.hideInputRowOnSticky]: sticky && minimized,
                                [styles.roundTrip]: isRoundTrip,
                                [styles.withPassenger]: true,
                            },
                        )}
                    >
                        <ChikuHeroMobile className={styles.chikuHero} />
                        <RoundTripInputDropdown
                            cellClass={classNames(styles.cell)}
                            destinationInput={destinationInput}
                            destinationPrediction={destinationPrediction}
                            equalInputs={equalInputs}
                            highlightClass={styles.highlighted}
                            loading={loading}
                            missingDestination={missingDestination}
                            missingOrigin={missingOrigin}
                            onDestinationChange={handleDestinationChange}
                            onDestinationInputChange={handleDestinationInputChange}
                            onFocus={onFocus}
                            onOriginChange={handleOriginChange}
                            onOriginInputChange={handleOriginInputChange}
                            onSwitch={switchInputs}
                            originInput={originInput}
                            originPrediction={originPrediction}
                            rowClass={classNames(
                                styles.row,
                                styles.tripInput,
                                styles.tripArea,
                            )}
                            sticky={sticky}
                        />
                        <div
                            className={classNames(
                                styles.datePicker,
                                styles.dateArea,
                                {
                                    [styles.borderless]: isRoundTrip
                                        ? missingDepartDate || missingReturnDate
                                        : missingDepartDate,
                                },
                            )}
                        >
                            <TabPanel
                                className={styles.tabPanel}
                                id={sticky
                                    ? `${searchType}-sticky`
                                    : searchType}
                            >
                                {renderDatePicker(sticky)}
                            </TabPanel>
                        </div>
                        <div className={styles.passengerArea}>
                            <PassengerSelect
                                onChange={handlePassengerChange}
                                rowClass={styles.passengerSelect}
                                value={passengersInput}
                            />
                        </div>
                        <SearchButton
                            className={classNames(
                                styles.searchButton,
                                styles.desktop,
                                styles.buttonArea,
                            )}
                            disabled={disableButton}
                            loading={loading}
                            onClick={submitSearch}
                        />
                    </div>

                    {sticky && minimized && (
                        <MinimizedWidgetBody
                            departDate={departDate}
                            destinationInput={destinationInput}
                            disabled={disableButton}
                            locale={locale}
                            onSearch={submitSearch}
                            onToggle={handleToggle}
                            originInput={originInput}
                            returnDate={returnDate}
                            title={title}
                        />
                    )}
                    {sticky && !minimized && (
                        <Button
                            className={styles.chevronUp}
                            onClick={handleToggle}
                            unstyled
                        >
                            <BigChevronUp />
                        </Button>
                    )}
                </div>
                {!searchPage && (
                    <Checkbox
                        checked={ctzChecked}
                        className={classNames(
                            styles.checkbox,
                            { [styles.stickied]: sticky },
                        )}
                        disabled={loading}
                        labelClassName={styles.checkboxLabel}
                        labelText={t('baseApp:hotels-ad-title')}
                        onChange={toggleClicktrips}
                    />

                )}
            </TabContainer>
        </div >
    );
}

WidgetBody.propTypes = {
    ctzChecked: PropTypes.bool,
    currency: PropTypes.string,
    departDate: PropTypes.string,
    departPricing: PropTypes.object,
    destinationInput: PropTypes.string,
    destinationPlaceId: PropTypes.string,
    destinationPrediction: PropTypes.arrayOf(PropTypes.shape({
        placeId: PropTypes.string,
        text: PropTypes.string,
    })),
    disableButton: PropTypes.bool,
    emptyFields: PropTypes.shape({
        missingDepartDate: PropTypes.bool,
        missingDestination: PropTypes.bool,
        missingOrigin: PropTypes.bool,
        missingReturnDate: PropTypes.bool,
    }),
    equalInputs: PropTypes.bool,
    fetchPrediction: PropTypes.func,
    headerText: PropTypes.string,
    i18nMoment: PropTypes.func,
    i18nMoney: PropTypes.func,
    locale: PropTypes.string,
    minimized: PropTypes.bool,
    onDepartDateChange: PropTypes.func,
    onDestinationChange: PropTypes.func,
    onDestinationInputChange: PropTypes.func,
    onErrorMessageClear: PropTypes.func,
    onOriginChange: PropTypes.func,
    onOriginInputChange: PropTypes.func,
    onPassengerChange: PropTypes.func,
    onReturnDateChange: PropTypes.func,
    onTabChange: PropTypes.func,
    onToggle: PropTypes.func,
    onUserInputChange: PropTypes.func,
    onUserSearch: PropTypes.func,
    originInput: PropTypes.string,
    originPlaceId: PropTypes.string,
    originPrediction: PropTypes.arrayOf(PropTypes.shape({
        placeId: PropTypes.string,
        text: PropTypes.string,
    })),
    passengersInput: PropTypes.string,
    returnDate: PropTypes.string,
    returnPricing: PropTypes.object,
    searchPage: PropTypes.bool,
    searchType: PropTypes.oneOf(SEARCH_TYPES),
    setDestinationInput: PropTypes.func,
    setDestinationPlaceId: PropTypes.func,
    setOriginInput: PropTypes.func,
    setOriginPlaceId: PropTypes.func,
    setPassengersInput: PropTypes.func,
    sticky: PropTypes.bool,
    t: PropTypes.func,
    tabBlack: PropTypes.bool,
    title: PropTypes.string,
    toggleClicktrips: PropTypes.func,
};

export { WidgetBody };
export default localize(WidgetBody, ['i18nMoney', 'i18nMoment', 't'], 'widget');
